import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useRef, useState } from 'react';
import { Collapse, FormHelperText, TextField as MuiTextField } from '@material-ui/core';
import cn from 'classnames';
import { ArrowSolidDown } from '../../../icons';
import { preventBadNumberInputKeys, preventBadNumberInputValues } from '../../../input-management';
import { isValidNumberWithPrecisionOf2, isValidPercentageWithPrecisionOf2 } from '../../../utils/money-input';
import { useBiAction } from '../../BiTracking';
import { Box } from '../../Layout';
import { useBaseTextFieldStyles } from './BaseTextField.styles';
import { TextFieldTestIds } from './BaseTextField.utils';
export const BaseTextField = /*#__PURE__*/ forwardRef((props, ref)=>{
    var _props_InputProps, _props_InputProps1, _props_InputProps2;
    const { type, children, className, prefix, suffix, disableSuffixPadding, noWrapSuffix, label, required, success, testId, inputProps, size = 'medium', menuMaxHeight, width, minWidth, isMoney, isPercentage, alwaysShrinkLabel, variant = 'outlined', onKeyDown, onChange, warning, preventOutline, placeholder, value, isSignature, displayValue, inputRootClass, helperTextReserveSpace, textColor, select, SelectProps, collapseHelperText = true, showHelperTextOnFocusOnly = false, helperText, disabled, error, bi } = props, rest = _object_without_properties(props, [
        "type",
        "children",
        "className",
        "prefix",
        "suffix",
        "disableSuffixPadding",
        "noWrapSuffix",
        "label",
        "required",
        "success",
        "testId",
        "inputProps",
        "size",
        "menuMaxHeight",
        "width",
        "minWidth",
        "isMoney",
        "isPercentage",
        "alwaysShrinkLabel",
        "variant",
        "onKeyDown",
        "onChange",
        "warning",
        "preventOutline",
        "placeholder",
        "value",
        "isSignature",
        "displayValue",
        "inputRootClass",
        "helperTextReserveSpace",
        "textColor",
        "select",
        "SelectProps",
        "collapseHelperText",
        "showHelperTextOnFocusOnly",
        "helperText",
        "disabled",
        "error",
        "bi"
    ]);
    const [isFocused, setIsFocused] = useState(false);
    const valueOnFocusRef = useRef('');
    const logBiAction = useBiAction();
    const effectiveHelperText = showHelperTextOnFocusOnly && !error && !isFocused ? undefined : helperText;
    const styles = useBaseTextFieldStyles({
        menuMaxHeight,
        width,
        minWidth,
        disableSuffixPadding,
        isSignature,
        helperTextReserveSpace,
        textColor
    });
    const actualType = isMoney || isPercentage ? 'number' : type;
    const isNumberType = actualType === 'number';
    const shouldHackSelectWithPlaceholder = select && placeholder;
    const extraSelectProps = shouldHackSelectWithPlaceholder ? {
        displayEmpty: true,
        renderValue: (value)=>{
            if (Array.isArray(value) ? value.length > 0 : value) {
                const selectedChild = React.Children.toArray(children).find((child)=>/*#__PURE__*/ React.isValidElement(child) && child.props.value === value);
                const selectLabel = /*#__PURE__*/ React.isValidElement(selectedChild) ? selectedChild.props.label || selectedChild.props.children : value;
                return /*#__PURE__*/ _jsx("div", {
                    "data-testid": TextFieldTestIds.label(testId),
                    className: styles.ellipsis,
                    children: displayValue || selectLabel
                });
            } else {
                return /*#__PURE__*/ _jsx("div", {
                    "data-testid": TextFieldTestIds.placeholder(testId),
                    className: styles.selectPlaceholder,
                    children: placeholder
                });
            }
        }
    } : {};
    const endAdornmentContent = suffix != null ? Array.isArray(suffix) ? /*#__PURE__*/ _jsx(Box, {
        gap: 2,
        children: suffix
    }) : suffix : (_props_InputProps = props.InputProps) === null || _props_InputProps === void 0 ? void 0 : _props_InputProps.endAdornment;
    const endAdornment = noWrapSuffix && endAdornmentContent ? /*#__PURE__*/ _jsx("span", {
        "data-testid": TextFieldTestIds.noWrapSuffix(testId),
        className: styles.noWrapSuffix,
        children: endAdornmentContent
    }) : endAdornmentContent;
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.wrapper, className),
        children: [
            /*#__PURE__*/ _jsx(MuiTextField, _object_spread_props(_object_spread({
                innerRef: ref,
                "data-testid": TextFieldTestIds.root(testId)
            }, rest), {
                select: select,
                placeholder: placeholder,
                value: value,
                type: actualType,
                className: cn({
                    [styles.success]: success,
                    [styles.warning]: warning,
                    [styles.noOutline]: preventOutline
                }, styles.root),
                variant: variant,
                label: required && label ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        label,
                        /*#__PURE__*/ _jsx("span", {
                            className: "sg-tf-asterix",
                            children: "*"
                        })
                    ]
                }) : label,
                onChange: isNumberType ? (e)=>{
                    const newValue = e.currentTarget.value;
                    if (Number.isNaN(+newValue)) return;
                    preventBadNumberInputValues(e);
                    if (isMoney && !isValidNumberWithPrecisionOf2(newValue)) return;
                    if (isPercentage && !isValidPercentageWithPrecisionOf2(newValue)) return;
                    onChange && onChange(e);
                } : (e)=>{
                    if (select && (bi === null || bi === void 0 ? void 0 : bi.name)) {
                        logBiAction({
                            entityType: 'select',
                            entityName: bi.name,
                            actionType: 'select',
                            data: _object_spread_props(_object_spread({}, bi.data), {
                                test_id: testId !== null && testId !== void 0 ? testId : '',
                                selected_value: String(e.target.value)
                            })
                        });
                    }
                    onChange === null || onChange === void 0 ? void 0 : onChange(e);
                },
                onKeyDown: isNumberType ? (e)=>{
                    preventBadNumberInputKeys(e, inputProps && inputProps.min != null && inputProps.min >= 0);
                    onKeyDown && onKeyDown(e);
                } : undefined,
                InputProps: {
                    ref: (_props_InputProps1 = props.InputProps) === null || _props_InputProps1 === void 0 ? void 0 : _props_InputProps1.ref,
                    startAdornment: prefix || ((_props_InputProps2 = props.InputProps) === null || _props_InputProps2 === void 0 ? void 0 : _props_InputProps2.startAdornment),
                    endAdornment,
                    required,
                    classes: {
                        root: cn(styles.inputRoot, inputRootClass),
                        input: cn(styles.inputInput, styles["inputInput_".concat(size)])
                    }
                },
                SelectProps: _object_spread({
                    ['data-testid']: TextFieldTestIds.Select(),
                    IconComponent: ArrowSolidDown,
                    MenuProps: {
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                        },
                        getContentAnchorEl: null,
                        autoFocus: false,
                        classes: {
                            paper: styles.menu
                        },
                        ['data-testid']: TextFieldTestIds.backdrop(testId)
                    }
                }, extraSelectProps, SelectProps),
                InputLabelProps: {
                    ['data-testid']: TextFieldTestIds.label(testId),
                    classes: {
                        outlined: cn(styles.label, styles["label_".concat(size)])
                    },
                    shrink: alwaysShrinkLabel || shouldHackSelectWithPlaceholder || undefined
                },
                inputProps: _object_spread(_object_spread_props(_object_spread({
                    ['data-testid']: TextFieldTestIds.input(testId),
                    step: actualType === 'number' ? 'any' : undefined
                }, isNumberType ? {
                    inputMode: 'numeric'
                } : {}), {
                    onInvalid: (e)=>{
                        e.preventDefault();
                    },
                    onWheel: isNumberType ? (e)=>{
                        // Prevent scroll wheel from changing the value in number inputs
                        e.currentTarget.blur();
                    } : inputProps === null || inputProps === void 0 ? void 0 : inputProps.onWheel
                }), inputProps),
                FormHelperTextProps: {
                    ['data-testid']: TextFieldTestIds.helperText(testId),
                    classes: {
                        contained: cn(styles.helperText, {
                            positionFixed: !helperTextReserveSpace
                        })
                    }
                },
                onFocus: (e)=>{
                    var _rest_onFocus;
                    setIsFocused(true);
                    valueOnFocusRef.current = e.target.value;
                    (_rest_onFocus = rest.onFocus) === null || _rest_onFocus === void 0 ? void 0 : _rest_onFocus.call(rest, e);
                },
                onBlur: (e)=>{
                    var _rest_onBlur;
                    setIsFocused(false);
                    if (!select && (bi === null || bi === void 0 ? void 0 : bi.name)) {
                        logBiAction({
                            entityType: 'text_field',
                            entityName: bi.name,
                            actionType: 'text_input',
                            data: _object_spread_props(_object_spread({}, bi.data), {
                                test_id: testId !== null && testId !== void 0 ? testId : '',
                                has_changed: valueOnFocusRef.current !== e.target.value
                            })
                        });
                    }
                    (_rest_onBlur = rest.onBlur) === null || _rest_onBlur === void 0 ? void 0 : _rest_onBlur.call(rest, e);
                },
                helperText: collapseHelperText ? undefined : effectiveHelperText,
                error: error,
                disabled: disabled,
                children: children
            })),
            collapseHelperText && /*#__PURE__*/ _jsx(Collapse, {
                in: !!effectiveHelperText,
                unmountOnExit: true,
                className: styles.collapseWrapper,
                children: /*#__PURE__*/ _jsx(FormHelperText, {
                    "data-testid": TextFieldTestIds.helperText(testId),
                    error: error,
                    disabled: disabled && !error,
                    className: cn(styles.helperText, styles.helperTextCollapse),
                    children: effectiveHelperText || '\u00A0'
                })
            })
        ]
    });
});
